import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft, Save } from "lucide-react";
import type { ReactNode } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import {
  FieldError,
  FieldLabel,
  Input,
  Select,
  Textarea,
} from "@/components/ui/Field";
import {
  FIXED_RATE_UNITS,
  QUANTITY_RATE_UNITS,
  RATE_UNIT_LABELS,
  currencyOptionLabel,
} from "@/domain/billing";
import {
  CURRENCIES,
  type RateUnitPreset,
} from "@/domain/types";
import {
  clientFormSchema,
  type ClientFormValues,
} from "@/features/invoices/client-form";
import { DueRuleFields } from "./DueRuleFields";

interface Props {
  initialValues: ClientFormValues;
  submitLabel: string;
  onSubmit: (
    values: ClientFormValues,
  ) => void | Promise<void>;
}

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <Card className="p-5 sm:p-6">
      <div className="mb-5">
        <h2 className="text-lg font-black text-ink">
          {title}
        </h2>
        <p className="mt-1 text-sm leading-6 text-ink-muted">
          {description}
        </p>
      </div>
      {children}
    </Card>
  );
}

function RateUnitOptions({
  units,
}: {
  units: RateUnitPreset[];
}) {
  return (
    <>
      {units.map((unit) => (
        <option key={unit} value={unit}>
          {unit === "custom"
            ? "Custom unit"
            : `per ${RATE_UNIT_LABELS[unit]}`}
        </option>
      ))}
    </>
  );
}

export function ClientForm({
  initialValues,
  submitLabel,
  onSubmit,
}: Props) {
  const {
    register,
    watch,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<ClientFormValues>({
    resolver: zodResolver(clientFormSchema),
    defaultValues: initialValues,
    mode: "onBlur",
  });

  const billingType = watch("billingType");
  const rateUnitPreset = watch("rateUnitPreset");
  const currency = watch("currency");

  const unitOptions =
    billingType === "hourly"
      ? (["hour"] as RateUnitPreset[])
      : billingType === "fixed"
        ? FIXED_RATE_UNITS
        : QUANTITY_RATE_UNITS;

  const setBillingType = (
    nextType: ClientFormValues["billingType"],
  ) => {
    setValue("billingType", nextType, {
      shouldDirty: true,
    });

    if (nextType === "hourly") {
      setValue("rateUnitPreset", "hour", {
        shouldDirty: true,
      });
      setValue("customRateUnit", "");
    } else if (nextType === "fixed") {
      setValue("rateUnitPreset", "project", {
        shouldDirty: true,
      });
    } else {
      setValue("rateUnitPreset", "item", {
        shouldDirty: true,
      });
    }
  };

  const rateLabel =
    billingType === "hourly"
      ? `Default hourly rate (${currency})`
      : billingType === "fixed"
        ? `Default fixed amount (${currency})`
        : `Default price per unit (${currency})`;

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      className="space-y-6"
    >
      <Section
        title="Client information"
        description="Saved profiles make future invoices faster while keeping each invoice independently editable."
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <FieldLabel required htmlFor="client-name">
              Client name
            </FieldLabel>
            <Input
              id="client-name"
              {...register("name")}
            />
            <FieldError message={errors.name?.message} />
          </div>

          <div>
            <FieldLabel htmlFor="client-company">
              Company
            </FieldLabel>
            <Input
              id="client-company"
              {...register("company")}
            />
          </div>

          <div>
            <FieldLabel htmlFor="client-email">
              Email
            </FieldLabel>
            <Input
              id="client-email"
              type="email"
              {...register("email")}
            />
            <FieldError message={errors.email?.message} />
          </div>

          <div>
            <FieldLabel htmlFor="client-phone">
              Phone
            </FieldLabel>
            <Input
              id="client-phone"
              {...register("phone")}
            />
          </div>

          <div className="sm:col-span-2">
            <FieldLabel htmlFor="client-address-1">
              Address line 1
            </FieldLabel>
            <Input
              id="client-address-1"
              {...register("address.line1")}
            />
          </div>

          <div className="sm:col-span-2">
            <FieldLabel htmlFor="client-address-2">
              Address line 2
            </FieldLabel>
            <Input
              id="client-address-2"
              {...register("address.line2")}
            />
          </div>

          <div>
            <FieldLabel htmlFor="client-city">
              City
            </FieldLabel>
            <Input
              id="client-city"
              {...register("address.city")}
            />
          </div>

          <div>
            <FieldLabel htmlFor="client-state">
              State / province
            </FieldLabel>
            <Input
              id="client-state"
              {...register("address.state")}
            />
          </div>

          <div>
            <FieldLabel htmlFor="client-postal">
              Postal code
            </FieldLabel>
            <Input
              id="client-postal"
              {...register("address.postalCode")}
            />
          </div>

          <div>
            <FieldLabel htmlFor="client-country">
              Country
            </FieldLabel>
            <Input
              id="client-country"
              {...register("address.country")}
            />
          </div>
        </div>
      </Section>

      <Section
        title="Default billing arrangement"
        description="Set the client's preferred currency, calculation method, price unit, billing cycle, and due agreement. Every invoice can override these defaults."
      >
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <FieldLabel htmlFor="client-currency">
              Default currency
            </FieldLabel>
            <Select
              id="client-currency"
              {...register("currency")}
            >
              {CURRENCIES.map((currencyCode) => (
                <option
                  key={currencyCode}
                  value={currencyCode}
                >
                  {currencyOptionLabel(currencyCode)}
                </option>
              ))}
            </Select>
          </div>

          <div>
            <FieldLabel htmlFor="client-billing-type">
              Default calculation
            </FieldLabel>
            <Select
              id="client-billing-type"
              value={billingType}
              onChange={(event) =>
                setBillingType(
                  event.target
                    .value as ClientFormValues["billingType"],
                )
              }
            >
              <option value="hourly">Hourly</option>
              <option value="fixed">Fixed amount</option>
              <option value="quantity">
                Quantity × rate
              </option>
            </Select>
          </div>

          <div>
            <FieldLabel htmlFor="client-rate-unit">
              Default price unit
            </FieldLabel>
            <Select
              id="client-rate-unit"
              {...register("rateUnitPreset")}
              disabled={billingType === "hourly"}
            >
              <RateUnitOptions units={unitOptions} />
            </Select>
          </div>

          {rateUnitPreset === "custom" ? (
            <div>
              <FieldLabel
                required
                htmlFor="client-custom-rate-unit"
              >
                Custom price unit
              </FieldLabel>
              <Input
                id="client-custom-rate-unit"
                placeholder="e.g. CRM setup"
                {...register("customRateUnit")}
              />
              <FieldError
                message={errors.customRateUnit?.message}
              />
            </div>
          ) : null}

          <div>
            <FieldLabel htmlFor="client-rate">
              {rateLabel}
            </FieldLabel>
            <Input
              id="client-rate"
              type="number"
              min="0"
              step="0.01"
              {...register("rate", {
                valueAsNumber: true,
              })}
            />
            <FieldError message={errors.rate?.message} />
          </div>

          <div>
            <FieldLabel htmlFor="client-cycle">
              Default billing cycle
            </FieldLabel>
            <Select
              id="client-cycle"
              {...register("billingCycle")}
            >
              <option value="one_time">One-time</option>
              <option value="weekly">Weekly</option>
              <option value="biweekly">Bi-weekly</option>
              <option value="monthly">
                Monthly / retainer
              </option>
              <option value="custom">Custom</option>
            </Select>
          </div>
        </div>

        <div className="mt-6">
          <DueRuleFields
            register={register}
            watch={watch}
            errors={errors}
          />
        </div>

        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <div>
            <FieldLabel htmlFor="client-payment-terms">
              Default payment terms
            </FieldLabel>
            <Textarea
              id="client-payment-terms"
              {...register("paymentTerms")}
            />
          </div>

          <div>
            <FieldLabel htmlFor="client-payment-instructions">
              Default payment instructions
            </FieldLabel>
            <Textarea
              id="client-payment-instructions"
              placeholder="e.g. GCash, Maya, Wise, or bank details"
              {...register("paymentInstructions")}
            />
          </div>
        </div>
      </Section>

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Link
          to="/app/clients"
          className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-border bg-white px-4 text-sm font-semibold text-ink hover:bg-surface-muted"
        >
          <ArrowLeft className="h-4 w-4" />
          Cancel
        </Link>

        <Button
          type="submit"
          variant="brand"
          size="lg"
          disabled={isSubmitting}
        >
          <Save className="h-5 w-5" />
          {isSubmitting ? "Saving…" : submitLabel}
        </Button>
      </div>
    </form>
  );
}
